import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Card, Button, InputGroup, FormControl } from "react-bootstrap";
import { FaSearch, FaPlus, FaMinus, FaTimes, FaUser, FaExchangeAlt, FaBuilding } from "react-icons/fa";
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from "../context/AuthContext";
import AuditorAssignmentService from "../services/AuditorAssignmentService";
import OrganizationalUnitService from "../services/OrganizationalUnitService";
import Spinner from "./Spinner";
import "./styles/AuditorAssignment.css";

// Assignment Modal Component
const AssignmentModal = ({ isOpen, title, onClose, children, footer }) => {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="access-modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="access-modal-content"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="access-modal-header">
                            <h3 className="access-modal-title">{title}</h3>
                            <button className="access-modal-close" onClick={onClose}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="access-modal-body">
                            {children}
                        </div>
                        
                        {footer && (
                            <div className="access-modal-footer">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const AuditorAssignment = ({ darkMode }) => {
    const { user } = useContext(AuthContext);
    const [assignments, setAssignments] = useState([]);
    const [orgUnits, setOrgUnits] = useState([]);
    const [hierarchy, setHierarchy] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [unitSearch, setUnitSearch] = useState("");
    const [expandedUnits, setExpandedUnits] = useState({});
    const [showModal, setShowModal] = useState(false);
    
    const [modalData, setModalData] = useState({
        type: 'unit', // unit or user
        sourceId: null,
        sourceName: '',
        workflowName: '',
        entityTypeName: '',
        entityTypeId: null,
        workflowId: "",
        role: "",
        isActive: true,
        notes: ""
    });


    // Fetch all assignments and organizational units
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch organizational units first - same way as WorkflowAssignment
                const unitsData = await OrganizationalUnitService.getOrganizationChart();
                
                if (!unitsData || unitsData.length === 0) {
                    setError('No organizational units found');
                    setOrgUnits([]);
                    setLoading(false);
                    
                    // Still try to fetch assignments
                    try {
                        const assignmentsData = await AuditorAssignmentService.getAll();
                        setAssignments(assignmentsData || []);
                    } catch (assignErr) {
                        console.error("Error fetching assignments:", assignErr);
                    }
                    return;
                }
                
                setOrgUnits(unitsData);
                
                // Initialize expanded states for units
                const unitExpanded = {};
                const initializeExpandedState = (units) => {
                    units.forEach(unit => {
                        unitExpanded[unit.id] = false;
                        if (unit.children && unit.children.length > 0) {
                            initializeExpandedState(unit.children);
                        }
                    });
                };
                initializeExpandedState(unitsData);
                setExpandedUnits(unitExpanded);
                
                // Fetch assignments
                const assignmentsData = await AuditorAssignmentService.getAll();
                setAssignments(assignmentsData || []);

                // Fetch process/workflow/entity-type hierarchy for right-side targets
                const hierarchyData = await AuditorAssignmentService.getHierarchy();
                setHierarchy(hierarchyData || []);
                
                setLoading(false);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError(err.response?.data?.message || err.message || "Failed to load data");
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Toggle expanded state for units
    const toggleUnitExpand = (unitId) => {
        setExpandedUnits(prev => ({
            ...prev,
            [unitId]: !prev[unitId]
        }));
    };

    // Filter units based on search term
    const filterUnits = (units, searchTerm) => {
        if (!searchTerm) return units;
        return units.filter(unit => {
            const matchesName = unit.name.toLowerCase().includes(searchTerm.toLowerCase());
            const hasMatchingChildren = unit.children && filterUnits(unit.children, searchTerm).length > 0;
            const hasMatchingUsers = (unit.assignedUsers || unit.users || []).some(user =>
                (user.userFullName || user.fullName || user.name || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
            return matchesName || hasMatchingChildren || hasMatchingUsers;
        });
    };

    // Find unit by id
    const findUnit = (units, id) => {
        for (const unit of units) {
            if (String(unit.id) === String(id)) return unit;
            if (unit.children && unit.children.length > 0) {
                const found = findUnit(unit.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const findUser = (units, userId) => {
        for (const unit of units) {
            const unitUsers = unit.assignedUsers || unit.users || [];
            const foundUser = unitUsers.find(u =>
                String(u.userId) === String(userId) || String(u.id) === String(userId)
            );
            if (foundUser) return foundUser;

            if (unit.children && unit.children.length > 0) {
                const found = findUser(unit.children, userId);
                if (found) return found;
            }
        }
        return null;
    };

    // Handle drag end event for drag-and-drop
    const onDragEnd = (result) => {
        const { destination, draggableId } = result;

        if (!destination) return;

        const draggedItemType = draggableId.split('-')[0];
        const draggedItemId = draggableId.split('-')[1];

        if (!destination.droppableId.startsWith('entity-')) return;

        const targetParts = destination.droppableId.split('-');
        if (targetParts.length < 4) return;

        const workflowId = targetParts[1];
        const entityTypeId = targetParts[2];
        const encodedEntityTypeName = targetParts.slice(3).join('-');
        const entityTypeName = decodeURIComponent(encodedEntityTypeName);

        let workflowName = '';
        for (const processType of hierarchy || []) {
            const wf = (processType.workflows || []).find(w => String(w.id) === String(workflowId));
            if (wf) {
                workflowName = wf.name || '';
                break;
            }
        }

        if (draggedItemType === 'unit') {
            const unit = findUnit(orgUnits, draggedItemId);
            if (unit) {
                setModalData({
                    type: 'unit',
                    sourceId: draggedItemId,
                    sourceName: unit.name,
                    workflowName: workflowName,
                    entityTypeName: entityTypeName,
                    entityTypeId: parseInt(entityTypeId),
                    workflowId: String(workflowId),
                    role: "",
                    isActive: true,
                    notes: ""
                });
                setShowModal(true);
            }
        } else if (draggedItemType === 'user') {
            const draggedUser = findUser(orgUnits, draggedItemId);
            if (draggedUser) {
                setModalData({
                    type: 'user',
                    sourceId: draggedItemId,
                    sourceName: draggedUser.userFullName || draggedUser.fullName || draggedUser.name || 'Unknown User',
                    workflowName: workflowName,
                    entityTypeName: entityTypeName,
                    entityTypeId: parseInt(entityTypeId),
                    workflowId: String(workflowId),
                    role: "",
                    isActive: true,
                    notes: ""
                });
                setShowModal(true);
            }
        }
    };

    // Render organizational unit tree (same structure as WorkflowAssignment)
    const renderUnitTree = (units, level = 0) => {
        if (!units || units.length === 0) return null;

        return (
            <ul className="unit-tree" style={{ paddingLeft: level > 0 ? '20px' : '0' }}>
                {units.map((unit, index) => (
                    <li key={`unit-${unit.id}`} className="unit-item">
                        <div className="unit-header">
                            <button
                                className="toggle-btn"
                                onClick={() => toggleUnitExpand(unit.id)}
                            >
                                {expandedUnits[unit.id] ? <FaMinus /> : <FaPlus />}
                            </button>
                            <Draggable draggableId={`unit-${unit.id}`} index={index} key={`unit-${unit.id}`}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`unit-label ${snapshot.isDragging ? 'dragging' : ''}`}
                                        style={{
                                            ...provided.draggableProps.style,
                                            backgroundColor: unit.unitTypeColor || '#3498db'
                                        }}
                                    >
                                        {unit.name}
                                    </div>
                                )}
                            </Draggable>
                        </div>

                        {expandedUnits[unit.id] && (
                            <>
                                {(unit.assignedUsers || unit.users || []).length > 0 ? (
                                    <ul className="user-list">
                                        {(unit.assignedUsers || unit.users || []).map((dragUser, userIndex) => (
                                            <div key={`user-${dragUser.userId || dragUser.id}`}>
                                                <Draggable draggableId={`user-${dragUser.userId || dragUser.id}`} index={userIndex}>
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className="user-label"
                                                        >
                                                            <FaUser style={{ marginRight: '5px' }} />
                                                            {dragUser.userFullName || dragUser.fullName || dragUser.name || 'Unknown User'}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            </div>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="no-users-message">No users assigned to this unit</div>
                                )}

                                {unit.children && unit.children.length > 0 && renderUnitTree(unit.children, level + 1)}
                            </>
                        )}
                    </li>
                ))}
            </ul>
        );
    };

    // Handle modal input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setModalData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    // Handle modal assignment
    const handleModalAssign = async () => {
        try {
            const assignmentData = {
                userId: modalData.type === 'user' ? parseInt(modalData.sourceId) : null,
                organizationalUnitId: modalData.type === 'unit' ? parseInt(modalData.sourceId) : null,
                workflowId: parseInt(modalData.workflowId),
                entityTypeId: modalData.entityTypeId,
                entityTypeName: modalData.entityTypeName,
                role: modalData.role,
                isActive: modalData.isActive,
                notes: modalData.notes
            };
            await AuditorAssignmentService.create(assignmentData);
            
            // Refresh assignments
            const updated = await AuditorAssignmentService.getAll();
            setAssignments(updated || []);
            setShowModal(false);
            setModalData({
                type: 'unit',
                sourceId: null,
                sourceName: '',
                workflowName: '',
                entityTypeName: '',
                entityTypeId: null,
                workflowId: "",
                role: "",
                isActive: true,
                notes: ""
            });
        } catch (err) {
            setError(err.response?.data?.message || "Failed to create assignment");
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowModal(false);
        setModalData({
            type: 'unit',
            sourceId: null,
            sourceName: '',
            workflowName: '',
            entityTypeName: '',
            entityTypeId: null,
            workflowId: "",
            role: "",
            isActive: true,
            notes: ""
        });
    };

    const getUserNameById = (units, userId) => {
        const foundUser = findUser(units, userId);
        return foundUser?.userFullName || foundUser?.fullName || foundUser?.name || `User #${userId}`;
    };

    const getUnitNameById = (units, unitId) => {
        const foundUnit = findUnit(units, unitId);
        return foundUnit?.name || `Unit #${unitId}`;
    };

    const renderWorkflowList = () => {
        if (!hierarchy || hierarchy.length === 0) {
            return <div className="no-data-message">No process/entity hierarchy found</div>;
        }

        return hierarchy.map(processType => (
            <div key={`process-${processType.id}`} className="workflow-group mb-3">
                <div className="workflow-group-title">{processType.name}</div>

                {(processType.workflows || []).map(workflow => (
                    <div key={`workflow-wrap-${workflow.id}`} className="list-group-item mb-2">
                        <div className="d-flex align-items-start mb-2">
                            <div className="me-3 mt-1">
                                <FaExchangeAlt size={20} style={{ color: '#007bff' }} />
                            </div>
                            <div className="flex-grow-1">
                                <h5 className="mb-1">{workflow.name}</h5>
                                <small className="text-muted d-block">Process: {processType.name}</small>
                            </div>
                        </div>

                        <div className="entity-types-container">
                            {(workflow.entityTypes || []).length > 0 ? (
                                (workflow.entityTypes || []).map(entityType => {
                                    const droppableId = `entity-${workflow.id}-${entityType.id}-${encodeURIComponent(entityType.name)}`;
                                    const entityAssignments = assignments.filter(a =>
                                        String(a.workflowId) === String(workflow.id) &&
                                        ((a.entityTypeId && String(a.entityTypeId) === String(entityType.id)) ||
                                            ((a.entityTypeName || '').toLowerCase() === (entityType.name || '').toLowerCase()))
                                    );

                                    return (
                                        <Droppable key={droppableId} droppableId={droppableId}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`workflow-drop-zone entity-drop-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                                                >
                                                    <div className="entity-type-title">{entityType.name}</div>

                                                    {entityAssignments.length > 0 && (
                                                        <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
                                                            {entityAssignments.filter(a => a.userId).map(assignment => (
                                                                <span key={assignment.id} className="assignment-tag user-assignment-tag">
                                                                    <FaUser style={{ fontSize: '0.8rem' }} />
                                                                    <span>{assignment.userName || getUserNameById(orgUnits, assignment.userId)}</span>
                                                                    <button
                                                                        className="btn-close"
                                                                        onClick={() => handleDelete(assignment.id)}
                                                                        title="Remove assignment"
                                                                    ></button>
                                                                </span>
                                                            ))}

                                                            {entityAssignments.filter(a => a.organizationalUnitId).map(assignment => (
                                                                <span key={assignment.id} className="assignment-tag unit-assignment-tag">
                                                                    <FaBuilding style={{ fontSize: '0.8rem' }} />
                                                                    <span>{assignment.organizationalUnitName || getUnitNameById(orgUnits, assignment.organizationalUnitId)}</span>
                                                                    <button
                                                                        className="btn-close"
                                                                        onClick={() => handleDelete(assignment.id)}
                                                                        title="Remove assignment"
                                                                    ></button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    );
                                })
                            ) : (
                                <div className="no-data-message">No entity types available</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        ));
    };


    // Handle delete
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this assignment?")) {
            try {
                await AuditorAssignmentService.delete(id);
                const updatedAssignments = await AuditorAssignmentService.getAll();
                setAssignments(updatedAssignments);
                setError(null);
            } catch (err) {
                console.error("Error deleting auditor assignment:", err);
                setError(err.response?.data?.message || "Failed to delete auditor assignment");
            }
        }
    };

    if (loading) {
        return <Spinner />;
    }

    const filteredUnits = filterUnits(orgUnits, unitSearch);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Container fluid className="p-4 workflow-assignment-panel auditor-assignment-panel">
                <Row>
                    {/* Organizational Units Section - Left Panel */}
                    <Col md={4} className="mb-3 workflow-column">
                        <Card>
                            <Card.Header>
                                <h4 className={darkMode ? "text-white" : "text-dark"}>Organizational Units</h4>
                            </Card.Header>
                            <Card.Body>
                                <div className="search-container mb-3">
                                    <InputGroup>
                                        <FormControl
                                            placeholder="Search units..."
                                            value={unitSearch}
                                            onChange={(e) => setUnitSearch(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <FaSearch />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </div>
                                <Droppable droppableId="units-container" isDropDisabled={true}>
                                    {(provided) => (
                                        <div 
                                            className="tree-container"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {filteredUnits && filteredUnits.length > 0 ? (
                                                renderUnitTree(filteredUnits)
                                            ) : (
                                                <div className="no-data-message">No organizational units found</div>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Workflows Section - Right Panel */}
                    <Col md={8} className="workflow-column">
                        <Card>
                            <Card.Header>
                                <h4 className={`${darkMode ? "text-white" : "text-dark"} mb-0`}>Workflows</h4>
                                <small className="text-muted d-block mt-1">Drag unit/user to a workflow to assign</small>
                            </Card.Header>
                            <Card.Body>
                                {error && <div className="alert alert-danger">{error}</div>}
                                <div className="list-group">
                                    {renderWorkflowList()}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Assignment Modal */}
                <AssignmentModal
                    isOpen={showModal}
                    title={modalData.type === 'unit' ? 'Assign Unit to Workflow' : 'Assign User to Workflow'}
                    onClose={handleModalClose}
                    footer={
                        <div className="d-flex gap-2">
                            <Button variant="secondary" onClick={handleModalClose}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleModalAssign}>
                                Assign
                            </Button>
                        </div>
                    }
                >
                    <div className="assignment-modal-form">
                        {/* Assignment Information - From → To Flow */}
                        <div className="modal-selection-info">
                            <h3 className="assignment-title">Assignment Details</h3>
                            <div className="assignment-details">
                                <div className="assignment-item">
                                    <span className="assignment-label">From:</span>
                                    <span className="assignment-value">
                                        {modalData.type === 'unit' ? (
                                            <><FaBuilding style={{ marginRight: '5px' }} /> {modalData.sourceName}</>
                                        ) : (
                                            <><FaUser style={{ marginRight: '5px' }} /> {modalData.sourceName}</>
                                        )}
                                    </span>
                                </div>
                                <div className="assignment-arrow">→</div>
                                <div className="assignment-item">
                                    <span className="assignment-label">To:</span>
                                    <span className="assignment-value">
                                        <FaExchangeAlt style={{ marginRight: '5px' }} />
                                        {modalData.workflowName}
                                        {modalData.entityTypeName && (
                                            <span className="entity-type-badge">{modalData.entityTypeName}</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Role</label>
                            <FormControl
                                as="select"
                                name="role"
                                value={modalData.role}
                                onChange={handleInputChange}
                            >
                                <option value="">Select a role</option>
                                <option value="Auditor">Auditor</option>
                                <option value="QualityAssurance">Quality Assurance</option>
                                <option value="Reviewer">Reviewer</option>
                                <option value="Supervisor">Supervisor</option>
                                <option value="Manager">Manager</option>
                            </FormControl>
                        </div>
                    </div>
                </AssignmentModal>
            </Container>
        </DragDropContext>
    );
};

export default AuditorAssignment;
